import React from "react"
import { useDispatch } from "react-redux"

import { ENVIRONMENT, isTargetDatabase } from "@/base/environment"
import { seedTestDatabaseAsync } from "@/base/seed-test-database"
import { handleReloadApp } from "@/base/utility"
import { DEVICE_PERMISSIONS, FARM_PERMISSIONS } from "@/constants/permissions"
import {
  SEED_DATABASE_DEFAULTS,
  SEED_DATABASE_PHONE_NUMBER,
} from "@/constants/seed-database"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { setActiveFeature } from "@/redux/actions"
import { getIsSeedDatabaseOpenFromState } from "@/redux/selectors"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  Accordion,
  AlertDialog,
  AlertDialogScrollView,
  AppText,
  Box,
  Button,
  Checkbox,
  Column,
  Radio,
  Row,
  Theme,
} from "@/theme"

import { ActionButtons } from "./ActionButtons"
import { SetPermissionListItem } from "./PermissionListItem"

import type {
  SeedDatabaseOptions,
  VirtualDeviceType,
} from "@/base/seed-test-database"
import type { FarmUserPermissionName } from "@/constants/permissions"
import type { ButtonProps } from "@/theme"

export type SeedDatabasePreset = "demo" | "no-farms"

const CHECKBOX_OPTIONS = [
  {
    key: "activeUserIsAdmin",
    label: "Active user is admin",
  },
  {
    key: "termsOfServiceAccepted",
    label: "Terms of service accepted",
  },
  {
    key: "usesMetricSystem",
    label: "Uses metric system",
  },
] as const

const VIRTUAL_DEVICE_OPTIONS: Array<{
  label: string
  value: VirtualDeviceType
}> = [
  { label: "Unconfigured", value: "unconfigured" },
  { label: "Center Pivot Controller", value: "center_pivot_controller" },
]

/**
 * Admin only form for overriding seed database defaults
 */
export function SeedDatabaseOptionsForm({
  onClose,
}: {
  onClose: () => void
  // onSuccess?: () => void
}): React.JSX.Element {
  const [state, setState] = React.useState<SeedDatabaseOptions>({
    ...SEED_DATABASE_DEFAULTS,
  })

  const { dispatch, handleError, isLoading, toasts } =
    useRequestHelpers("SeedTestDatabase")
  // const router = useRouter()
  const handleSubmit = () => {
    if (!canSeedTestDatabase()) {
      throw new TypeError(
        `Cannot seed database in this environment: ${ENVIRONMENT.targetDatabaseName}`,
      )
    }
    dispatch(seedTestDatabaseAsync(state))
      .unwrap()
      .then(() => toasts.success("Database seeded"))
      .then(onClose)
      .then(() => {
        // return router.navigate("/")
        return handleReloadApp()
      })
      .catch((error) => {
        handleError(error, {
          toastMessage: "Failed to seed database",
        })
      })
  }
  const handleChangePermission = (
    key: FarmUserPermissionName,
    nextValue: boolean,
  ) => {
    setState((prev) => ({
      ...prev,
      activeUserPermissions: {
        ...prev.activeUserPermissions,
        [key]: nextValue,
      },
    }))
  }

  return (
    <Column flex={1} justifyContent={"space-between"}>
      <Box>
        <AppText>{`Api URL: ${ENVIRONMENT.apiUrl}`}</AppText>
        <AppText>{"Preset"}</AppText>
        <Row key={"presets"} mb={"$2"}>
          <Radio
            orientation={"horizontal"}
            selectedValue={state.presetName}
            options={[
              { label: "Default", value: "demo" },
              { label: "No Farms", value: "no-farms" },
            ]}
            onChange={(nextValue) =>
              setState((previous) => ({
                ...previous,
                presetName: nextValue as SeedDatabasePreset,
              }))
            }
          />
        </Row>
      </Box>
      <Box>
        {CHECKBOX_OPTIONS.map(
          ({ key, label }): React.JSX.Element => (
            <Checkbox
              key={key}
              isChecked={state[key] === true}
              title={label}
              onChange={() => {
                return setState((previous) => ({
                  ...previous,
                  [key]: !Boolean(previous[key]),
                }))
              }}
            />
          ),
        )}
      </Box>
      <Row>
        <AppText style={{ paddingRight: Theme.SPACING.$2 }}>
          {"Initial Phone Number"}
        </AppText>
        <Checkbox
          isChecked={Boolean(state.userPhoneNumbers)}
          onChange={(nextValue) => {
            if (nextValue) {
              setState((prev) => ({
                ...prev,
                userPhoneNumbers: [{ ...SEED_DATABASE_PHONE_NUMBER }],
              }))
            } else {
              setState((prev) => ({ ...prev, userPhoneNumbers: null }))
            }
          }}
        />
      </Row>
      {isTargetDatabase("LOCAL") ? (
        <Box>
          <Checkbox
            isChecked={state.createVirtualDevice !== null}
            title={"Create virtual device"}
            onChange={() => {
              setState((prev) => ({
                ...prev,
                createVirtualDevice:
                  prev.createVirtualDevice !== null ? null : "unconfigured",
              }))
            }}
          />
          {state.createVirtualDevice !== null ? (
            <Row ml={"$4"} mt={"$1"}>
              <Radio
                orientation={"horizontal"}
                selectedValue={state.createVirtualDevice}
                options={VIRTUAL_DEVICE_OPTIONS}
                onChange={(nextValue) =>
                  setState((prev) => ({
                    ...prev,
                    createVirtualDevice: nextValue as VirtualDeviceType,
                  }))
                }
              />
            </Row>
          ) : null}
        </Box>
      ) : null}
      {state.presetName === "demo" ? (
        <React.Fragment>
          <Accordion defaultIsOpen={false} titleText={"Device Permissions"}>
            {DEVICE_PERMISSIONS.getKeys().map((key) => {
              return (
                <SetPermissionListItem
                  key={key}
                  isEnabled={state.activeUserPermissions[key]}
                  name={key}
                  onValueChange={(nextValue) =>
                    handleChangePermission(key, nextValue)
                  }
                />
              )
            })}
          </Accordion>
          <Accordion defaultIsOpen={false} titleText={"Farm Permissions"}>
            {FARM_PERMISSIONS.getKeys().map((key) => {
              return (
                <SetPermissionListItem
                  key={key}
                  isEnabled={state.activeUserPermissions[key]}
                  name={key}
                  onValueChange={(nextValue) =>
                    handleChangePermission(key, nextValue)
                  }
                />
              )
            })}
          </Accordion>
        </React.Fragment>
      ) : null}
      <ActionButtons
        isLoading={isLoading}
        onPressCancel={onClose}
        onPressSubmit={handleSubmit}
      />
    </Column>
  )
}
/**
 * We should never attempt to seed the database in production or beta
 * (demo mode works differently)
 */
export function canSeedTestDatabase() {
  const targetDatabaseName = ENVIRONMENT.targetDatabaseName
  if (typeof targetDatabaseName !== "string") {
    return false
  }
  if (isTargetDatabase("PROD", "BETA")) {
    return false
  }
  return true
}

export function SeedDatabaseButton(props: ButtonProps) {
  const dispatch = useDispatch()
  if (!canSeedTestDatabase()) {
    return null
  }
  return (
    <Button
      IconComponent={"Database"}
      text={"Seed Database"}
      {...props}
      onPress={() => dispatch(setActiveFeature("seedDatabase"))}
    />
  )
}

export function SeedDatabaseDialog() {
  const isOpen = useRootSelector(getIsSeedDatabaseOpenFromState)
  const dispatch = useRootDispatch()
  const onClose = () => dispatch(setActiveFeature(undefined))

  return (
    <AlertDialog
      isOpen={isOpen}
      titleElement={"Seed DB " + `(${ENVIRONMENT.targetDatabaseName})`}
      onClose={onClose}
    >
      <AlertDialogScrollView>
        <SeedDatabaseOptionsForm onClose={onClose} />
      </AlertDialogScrollView>
    </AlertDialog>
  )
}
